/**
 * Path manipulation utilities
 *
 * This module provides helper functions for working with URL pathnames,
 * particularly for normalizing paths to ensure consistent comparisons
 * across the application.
 */

/**
 * Normalize pathname by removing trailing slash (except for root path "/")
 *
 * **Why this is needed**: TanStack Router's `location.pathname` may include
 * trailing slashes (e.g., "/onboarding/waitlist/"), which can cause
 * inconsistent path comparisons and unnecessary redirects.
 *
 * **Examples**:
 * - "/onboarding/waitlist/" → "/onboarding/waitlist"
 * - "/onboarding/waitlist" → "/onboarding/waitlist"
 * - "/" → "/"
 * - "" → "/"
 *
 * @param pathname - Pathname to normalize
 * @returns Normalized pathname without trailing slash (except for root)
 *
 * @example
 * ```ts
 * const normalized = normalizePath(location.pathname);
 * if (normalized === "/onboarding/waitlist") {
 *   // This will match both "/onboarding/waitlist" and "/onboarding/waitlist/"
 * }
 * ```
 */
export function normalizePath(pathname: string): string {
  // Handle empty string or root path
  if (!pathname || pathname === "/") {
    return "/";
  }

  // Remove trailing slash if present (but keep root path as is)
  return pathname.endsWith("/") && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
}

/**
 * Check if two pathnames are equivalent (ignoring trailing slashes)
 *
 * This is useful for comparing paths where one might have a trailing slash
 * and the other doesn't.
 *
 * @param path1 - First pathname
 * @param path2 - Second pathname
 * @returns True if paths are equivalent after normalization
 *
 * @example
 * ```ts
 * arePathsEquivalent("/onboarding/waitlist", "/onboarding/waitlist/"); // true
 * arePathsEquivalent("/onboarding/waitlist", "/onboarding/activation"); // false
 * ```
 */
export function arePathsEquivalent(path1: string, path2: string): boolean {
  return normalizePath(path1) === normalizePath(path2);
}
